import React, { useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { X as CloseIcon } from 'lucide-react';
import WorkerProfile from './WorkerProfile';
import { useLanguage } from '../../contexts/LanguageContext';
import { useViewpoint } from '../../contexts/ViewpointContext';
import { useTheme } from 'styled-components';

const DetailViewContainer = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  z-index: 1000;
  overflow-y: auto;
  overscroll-behavior: contain;
`;

const CloseButton = styled.button`
  position: fixed;
  top: ${({ theme, viewpoint }) => 
    viewpoint === 'mobile' || viewpoint === 'narrow' 
      ? theme.spacing.immersiveDetail[viewpoint].closeButton.top
      : '16px'};
  right: ${({ theme, viewpoint }) => 
    viewpoint === 'mobile' || viewpoint === 'narrow' 
      ? theme.spacing.immersiveDetail[viewpoint].closeButton.right
      : '16px'};
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.8);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1001;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.1);
    background: black;
  }
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: ${({ theme, viewpoint }) => 
    viewpoint === 'mobile' || viewpoint === 'narrow' 
      ? theme.spacing.immersiveDetail[viewpoint].contentPadding 
      : '16px'};
`;

const ProfileSection = styled.div`
  margin-bottom: ${({ theme, viewpoint }) => 
    viewpoint === 'mobile' || viewpoint === 'narrow' 
      ? theme.spacing.immersiveDetail[viewpoint].subtextToFeaturesGap 
      : '16px'};
`;

const FeaturesSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme, viewpoint }) => 
    viewpoint === 'mobile' || viewpoint === 'narrow' 
      ? theme.spacing.immersiveDetail[viewpoint].featuresGap 
      : '12px'};
  background: #f8f9fa;
  border-radius: 16px;
  padding: 20px;
  margin: 16px;
  margin-top: 0;
`;

const FeatureRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 16px;
  border-radius: 12px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;

  @media (max-width: 1024px) {
    align-items: flex-start;
  }

  &:hover {
    transform: translateX(4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  object-fit: cover;
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureTitle = styled.h3`
  margin: 0 0 4px 0;
  font-size: 18px;
  font-weight: 600;
  color: #000;
`;

const FeatureDescription = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #666;
`;

const ImmersiveDetailView = ({ worker, onClose }) => {
  const { t } = useLanguage();
  const { currentViewpoint } = useViewpoint();
  const theme = useTheme();

  const getImagePosition = () => {
    if (currentViewpoint === 'mobile' || currentViewpoint === 'narrow') {
      return theme.spacing.immersiveDetail[currentViewpoint].imageTopPosition;
    }
    return "center 20%";
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);
  
  return (
    <DetailViewContainer
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
    >
      <CloseButton onClick={onClose} viewpoint={currentViewpoint}>
        <CloseIcon size={24} />
      </CloseButton>
      
      <Content viewpoint={currentViewpoint}>
        <ProfileSection viewpoint={currentViewpoint}>
          <WorkerProfile
            workerKey={worker}
            shouldPlayVideo={true}
            customHeight={currentViewpoint === 'mobile' || currentViewpoint === 'narrow' 
              ? theme.spacing.immersiveDetail[currentViewpoint].cardHeight 
              : "380px"}
            customPosition={getImagePosition()}
            hideRainbowButton={true}
          />
        </ProfileSection>
        
        <FeaturesSection viewpoint={currentViewpoint}>
          {[1, 2, 3, 4].map((num) => (
            <FeatureRow key={num}>
              <FeatureImage
                src={t(`digitalWorkers.workers.${worker}.features.feature${num}.image`)}
                alt={t(`digitalWorkers.workers.${worker}.features.feature${num}.title`)}
              />
              <FeatureContent>
                <FeatureTitle>
                  {t(`digitalWorkers.workers.${worker}.features.feature${num}.title`)}
                </FeatureTitle>
                <FeatureDescription>
                  {t(`digitalWorkers.workers.${worker}.features.feature${num}.description`)}
                </FeatureDescription>
              </FeatureContent>
            </FeatureRow>
          ))}
        </FeaturesSection>
      </Content>
    </DetailViewContainer>
  );
};

export default ImmersiveDetailView; 